import { get, post, put } from './apiClient';
import { ApiResponse } from './tenantApi';

// Type definitions
export interface TenantConfig {
  tenantId: string;
  name: string;
  domain: string;
  clusterId: string;
  authProvider: string;
  status?: string;
  [key: string]: any; // Allow for additional properties
}

export interface ClusterConfig {
  clusterId: string;
  name: string;
  url: string;
  region: string;
  status: string;
  capacity: {
    maxTenants: number;
    currentTenants: number;
  };
  [key: string]: any; // Allow for additional properties
}

export interface ClusterCreate {
  id: string;
  name: string;
  url: string;
  region: string;
  status?: string;
  capacity?: {
    maxTenants: number;
    currentTenants: number;
  };
  [key: string]: any; // Allow for additional properties
}

/**
 * Get all tenant configurations
 * @returns Promise with tenant configurations array
 */
export const getAllTenantConfigs = (): Promise<ApiResponse<TenantConfig[]>> => {
  return get<ApiResponse<TenantConfig[]>>('/configurations/tenants');
};

/**
 * Get tenant configuration by ID
 * @param id Tenant ID
 * @returns Promise with tenant configuration
 */
export const getTenantConfig = (id: string): Promise<ApiResponse<TenantConfig>> => {
  return get<ApiResponse<TenantConfig>>(`/configurations/tenants/${id}`);
};

/**
 * Update tenant configuration
 * @param id Tenant ID
 * @param configData Configuration data to update
 * @returns Promise with updated configuration
 */
export const updateTenantConfig = (
  id: string,
  configData: Partial<TenantConfig>
): Promise<ApiResponse<TenantConfig>> => {
  return put<ApiResponse<TenantConfig>>(`/configurations/tenants/${id}`, configData);
};

/**
 * Get all cluster configurations
 * @returns Promise with cluster configurations array
 */
export const getAllClusterConfigs = (): Promise<ApiResponse<ClusterConfig[]>> => {
  return get<ApiResponse<ClusterConfig[]>>('/configurations/clusters');
};

/**
 * Get cluster configuration by ID
 * @param id Cluster ID
 * @returns Promise with cluster configuration
 */
export const getClusterConfig = (id: string): Promise<ApiResponse<ClusterConfig>> => {
  return get<ApiResponse<ClusterConfig>>(`/configurations/clusters/${id}`);
};

/**
 * Update cluster configuration
 * @param id Cluster ID
 * @param configData Configuration data to update
 * @returns Promise with updated configuration
 */
export const updateClusterConfig = (
  id: string,
  configData: Partial<ClusterConfig>
): Promise<ApiResponse<ClusterConfig>> => {
  return put<ApiResponse<ClusterConfig>>(`/configurations/clusters/${id}`, configData);
};

/**
 * Create new cluster configuration
 * @param clusterData Cluster data
 * @returns Promise with created cluster configuration
 */
export const createClusterConfig = (clusterData: ClusterCreate): Promise<ApiResponse<ClusterConfig>> => {
  return post<ApiResponse<ClusterConfig>>('/configurations/clusters', clusterData);
};

/**
 * Get tenants by cluster ID
 * @param clusterId Cluster ID
 * @returns Promise with tenants array
 */
export const getTenantsByCluster = (clusterId: string): Promise<ApiResponse<any[]>> => {
  return get<ApiResponse<any[]>>(`/configurations/clusters/${clusterId}/tenants`);
};